import { captureReadingContact } from "@/lib/api/chat";
import { trackEvent } from "@/lib/analytics-events";
import { rememberCheckoutPrefillFromContact } from "@/lib/checkout-prefill";
import { CAPTURE_POINTS } from "@/lib/contact-capture-points";
import { normalizePhoneE164 } from "@/lib/phone-e164";
import { KUNDALI_PHONE_CAPTURE_POLICY } from "@/lib/phone-capture-policy";

/**
 * Persist the chat-entry form's optional phone + optional email via the
 * EXISTING consent-correct backend (captureReadingContact → POST
 * /readings/{id}/contact), which accepts phone-only, email-only or both.
 * Phone is OPTIONAL (owner decision 2026-09-24): a seeker who leaves both blank
 * is still generated a reading and still chats — this is then a no-op. A saved
 * contact never grants marketing permission; that stays a separate explicit
 * opt-in (ADR-0043).
 *
 * Called AFTER the kundali/reading is created (so a real reading_id exists) so
 * the contact lands BEFORE message 1 on the chat surface.
 *
 * A contact the seeker DID give is a durability boundary, so the call retries
 * twice and then raises a user-retryable error instead of silently navigating
 * away with a chart that cannot be recognized later. Checkout prefill is saved
 * locally before the backend call. A typed phone that cannot normalize is
 * dropped (form validation already rejected it — defensive only).
 */
export async function persistFormContact(
  readingId: string | undefined | null,
  phone: string,
  email: string
): Promise<void> {
  if (!readingId) return;
  const normalizedPhone = normalizePhoneE164(phone) ?? undefined;
  const trimmedEmail = email.trim() || undefined;
  if (!normalizedPhone && !trimmedEmail) return;
  rememberCheckoutPrefillFromContact({ phone: normalizedPhone, email: trimmedEmail });
  const body = {
    phone: normalizedPhone,
    email: trimmedEmail,
    consent_marketing: KUNDALI_PHONE_CAPTURE_POLICY.consentMarketing,
  };
  let lastError: unknown;
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      await captureReadingContact(readingId, body);
      trackEvent("contact_captured", {
        reading_id: readingId,
        type: contactType(normalizedPhone, trimmedEmail),
        placement: "kundali-form-contact",
        capture_point: CAPTURE_POINTS.KUNDALI_FORM_CONTACT,
        consent_marketing: KUNDALI_PHONE_CAPTURE_POLICY.consentMarketing,
        attempt,
      });
      return;
    } catch (error) {
      lastError = error;
    }
  }
  trackEvent("contact_capture_failed", {
    reading_id: readingId,
    placement: "kundali-form-contact",
    capture_point: CAPTURE_POINTS.KUNDALI_FORM_CONTACT,
    attempts: 3,
  });
  throw new FormContactPersistenceError({ cause: lastError });
}

class FormContactPersistenceError extends Error {
  constructor(options?: ErrorOptions) {
    super(
      "Your kundali is ready, but we could not save your contact details. Please retry.",
      options
    );
    this.name = "FormContactPersistenceError";
  }
}

function contactType(phone: string | undefined, email: string | undefined) {
  if (!phone) return "email";
  return email ? "phone_email" : "phone";
}
